import { get } from "http";
import IconArrowDown from "~icons/ep/arrow-down-bold.svg";

import { Match, Show, Switch, createEffect, createSignal, on } from "solid-js";

import {
  AnimationMenuSlide,
  useDropdownMenu,
} from "@/components/Base/AnimationMenuSlide/index.tsx";
import LazyImg from "@/components/Base/LazyImg/index.tsx";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import Icon from "@/components/Icon.tsx";
import { getImageAltByLang } from "@/constants/seo-constants";
import { useRouter } from "@/hooks/useRouter";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import { getCurPointSymbol } from "@/lib/pointUtils";
import { formatToBindPoint, isFiat } from "@/lib/pointUtils.ts";
import { renderInnerHtml } from "@/lib/utils";
import { getCryptoIcon } from "@/stores/cryptoStore";
import { getUserInfo, userStore } from "@/stores/userStore";
import {
  bindCashCode,
  bindTotalPoint,
  walletStore,
} from "@/stores/walletStore.ts";

export default function WalletDropdownMenu() {
  const { showDialog } = useDialog();
  const { push } = useRouter();
  const [isPcOpen, setIsPcOpen] = createSignal(false);
  // 使用自定义 Hook 管理下拉菜单状态
  const {
    isOpen,
    isAnimating,
    toggleDropdown,
    startCloseAnimation,
    handleAnimationEnd,
  } = useDropdownMenu();

  // Close dropdown when clicking outside
  const handleOutsideClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const dropdown = document.getElementById("wallet-dropdown");
    const trigger = document.getElementById("wallet-dropdown-trigger");

    if (
      dropdown &&
      trigger &&
      !dropdown.contains(target) &&
      !trigger.contains(target)
    ) {
      startCloseAnimation();
    }
  };

  // Add event listener for outside clicks
  createEffect(
    on(isOpen, (boolean) => {
      if (boolean) {
        document.addEventListener("click", handleOutsideClick);
      } else {
        document.removeEventListener("click", handleOutsideClick);
      }

      return () => {
        document.removeEventListener("click", handleOutsideClick);
      };
    }),
  );

  const closeDropdown = () => {
    getUserInfo();

    if (isPcOpen()) {
      startCloseAnimation();
    } else {
      toggleDropdown();
    }

    setPcOpen();
  };
  const setPcOpen = () => setIsPcOpen(!isPcOpen());
  const handleAnimationEndReset = () => {
    handleAnimationEnd();
    setIsPcOpen(false);
  };

  return (
    <div
      class="relative"
      onclick={closeDropdown}
      // onMouseLeave={startCloseAnimation}
    >
      {/* Trigger */}
      <div
        id="wallet-dropdown-trigger"
        class="clickable flex cursor-pointer items-center"
      >
        <Icon name="money/fiat_BRL" class="size-[18px]" />
        <div class="ml-2 mr-1 flex flex-col items-start font-['Alexandria'] text-[14px] font-bold leading-[14px]">
          <div class="mb-[2px] text-[var(--colors-text-primary)]">
            {formatToBindPoint({
              value: walletStore.selectedWallet?.point,
              pointType: userStore.pointType || 0,
              showSymbol: false,
              isFiatPoint: isFiat(userStore.pointType || 0),
            })}

            {/* {formatToBindPoint({
              value: walletStore.selectedWallet?.bonus,
              pointType: walletStore.selectedWallet?.pointType || 0,
              showSymbol: false,
              isFiatPoint: isFiat(walletStore.selectedWallet?.pointType || 0),
            })} */}
          </div>
          {/* <div class="text-[var(--colors-text-warning)]">
            {formatToBindPoint({
              value: walletStore.selectedWallet?.bonus,
              pointType: userStore.pointType || 0,
              showSymbol: false,
              isFiatPoint: isFiat(userStore.pointType || 0),
            })}

          </div> */}
        </div>
        <Icon
          name="general/angle-down_2"
          class="size-[14px] text-[var(--colors-icon-invert-btn)]"
        />
      </div>

      {/* Dropdown content */}
      <Show when={isOpen() || isAnimating()} fallback={<></>}>
        <div
          id="wallet-dropdown"
          class="absolute right-[-72px] top-[28px] z-50 max-h-[480px] min-h-[231px] w-[510px] overflow-y-auto rounded-[10px] p-[15px]"
        >
          <AnimationMenuSlide
            state={isOpen()}
            onAnimationEnd={handleAnimationEndReset}
          >
            <div class="bg-general-bg4 flex flex-col gap-[15px] overflow-y-auto rounded-[10px] p-[15px]">
              {/* onclick={() => {
                  push("bonus-rule");
                  startCloseAnimation();
                }} */}
              <div class="flex cursor-pointer items-center gap-[6px]">
                <img
                  src={getCryptoIcon(
                    userStore.userInfo?.default_point_type || 0,
                  )}
                  class="h-[20px] w-[20px]"
                  alt="crypto_icon"
                />
                <span
                  class="text-general-light text-[16px] font-[500]"
                  classList={{
                    "leading-[18px]": isMobile(),
                    "leading-[19px]": !isMobile(),
                  }}
                >
                  {t("common.balance")}
                </span>
                {/* <div>
                  <Icon name="exclamation-mark1" class="h-[18px] w-[18px]" />
                </div> */}
              </div>
              <div class="border-1 border-general-sk flex h-[108px] items-center justify-center rounded-[8px] border-dashed pb-[22px] pt-[18px]">
                <div class="flex flex-col">
                  <span class="text-general-money text-center text-[28px] font-[600] leading-normal">
                    {bindTotalPoint()}
                  </span>
                  <Switch>
                    <Match when={!!walletStore.selectedWallet?.code}>
                      <div
                        class="text-general-darkzi text-center text-[14px] font-medium"
                        innerHTML={`<span>${t("wallet.bet_can_withdraw", {
                          val: renderInnerHtml(
                            bindCashCode(),
                            "text-general-light",
                          ),
                        })}</span>`}
                      ></div>
                    </Match>
                    <Match when={!walletStore.selectedWallet?.code}>
                      <div class="text-general-darkzi text-center text-[14px] font-medium">
                        {t("wallet.withdraw_anytime")}
                      </div>
                    </Match>
                  </Switch>
                  {/* <Switch>
                    <Match when={walletStore.pointStatus === "rollover"}>
                      <span class="text-general-darkzi text-[14px] font-[500] leading-normal">
                        {t("betmatch.wager")}{" "}
                        <span class="text-general-light text-[15px]">
                          {getCurPointSymbol()}
                          {formatToBindPoint({
                            value: walletStore.selectedWallet?.bonusCode,
                            pointType:
                              walletStore.selectedWallet?.pointType || 0,
                            showSymbol: false,
                            isFiatPoint: isFiat(
                              walletStore.selectedWallet?.pointType || 0,
                            ),
                          })}{" "}
                        </span>
                        {t("withdraw.redeemablepoints")}
                      </span>
                    </Match>
                    <Match when={walletStore.pointStatus === "redeemable"}>
                      <span class="text-general-darkzi text-[16px] font-[500] leading-normal">
                        {t("withdraw.points_redeemable_tip")}
                      </span>
                    </Match>
                    <Match when={walletStore.pointStatus === "none"}>
                      <span class="text-general-darkzi text-[16px] font-[500] leading-normal">
                        {t("bonus.join_event_points")}
                      </span>
                    </Match>
                  </Switch> */}
                </div>
              </div>
              <div
                onclick={() => {
                  showDialog({ type: DialogType.BillFlow });
                  startCloseAnimation();
                }}
                class="bg-general-tmd10 flex h-[44px] cursor-pointer items-center justify-center gap-[6px] rounded-[8px]"
              >
                <div class="flex items-center gap-1">
                  <span class="text-general-light text-[14px] font-[500] leading-normal">
                    {t("withdraw.rollover_details")}
                  </span>
                  <Icon name="double-chevron" class="h-[14px] w-[14px]" />
                </div>
                {/* <Switch>
                  <Match when={walletStore.pointStatus === "rollover"}>
                    <div
                      class="flex items-center gap-1"
                      onclick={() => {
                        showDialog({ type: DialogType.BillFlow });
                        startCloseAnimation();
                      }}
                    >
                      <span class="text-general-light text-[14px] font-[500] leading-normal">
                        {t("withdraw.rollover_details")}
                      </span>
                      <Icon name="double-chevron" class="h-[14px] w-[14px]" />
                    </div>
                  </Match>
                  <Match when={walletStore.pointStatus === "redeemable"}>
                    <div
                      class="flex items-center gap-1"
                      onclick={() => {
                        push("bonus-center");
                        startCloseAnimation();
                      }}
                    >
                      <span class="text-general-highbut text-[14px] font-[500] leading-normal">
                        {t("withdraw.points_redeemable_now")}
                      </span>
                      <Icon
                        name="double-chevron"
                        class="text-general-highbut h-[14px] w-[14px]"
                      />
                    </div>
                  </Match>
                  <Match when={walletStore.pointStatus === "none"}>
                    <div
                      onclick={() => {
                        push("bonus-center");
                        startCloseAnimation();
                      }}
                      class="flex items-center gap-1"
                    >
                      <span class="text-general-light text-[14px] font-[500] leading-normal">
                        {t("withdraw.earn_points")}
                      </span>
                      <Icon name="double-chevron" class="h-[14px] w-[12px]" />
                    </div>
                  </Match>
                </Switch> */}
              </div>
            </div>
          </AnimationMenuSlide>
        </div>
      </Show>
    </div>
  );
}
